// ============================================================
// 阿拉德战记 — WebSocket Client
// 联机组队客户端模块 · Phase 1: 基础设施
// ============================================================
// 负责：连接管理 / 消息收发 / 心跳 / 自动重连 / 事件分发
// 暴露到 window.mpClient 供全局访问
// ============================================================

const WS_URL = (() => {
  // 通过 Nginx /ws 代理，无需关心端口
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
})();

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000]; // 指数退避
const HEARTBEAT_INTERVAL = 25000; // 25s 发一次 ping

class MPClient {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.authenticated = false;
    this.userId = null;
    this.username = null;
    this.partyId = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.handlers = {};  // event → [callback, ...]

    // auto-connect if we have a token
    this._pendingToken = null;
  }

  // ===== Event System =====
  on(event, cb) {
    if (!this.handlers[event]) this.handlers[event] = [];
    this.handlers[event].push(cb);
  }

  off(event, cb) {
    if (!this.handlers[event]) return;
    this.handlers[event] = this.handlers[event].filter(f => f !== cb);
  }

  emit(event, data) {
    const cbs = this.handlers[event];
    if (cbs) {
      for (const cb of cbs) {
        try { cb(data); } catch (e) { console.error(`[MP] Handler error (${event}):`, e); }
      }
    }
  }

  // ===== Connection =====
  connect(token) {
    if (token) this._pendingToken = token;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    console.log(`[MP] Connecting to ${WS_URL}...`);
    this.ws = new WebSocket(WS_URL);

    this.ws.onopen = () => {
      console.log('[MP] Connected');
      this.connected = true;
      this.reconnectAttempts = 0;
      this.emit('connected');

      // authenticate
      const token = this._pendingToken || (typeof window.getAuthToken === 'function' ? window.getAuthToken() : null);
      if (token) {
        this._send('auth', { token });
      }

      // start heartbeat
      this._startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      this._handle(msg);
    };

    this.ws.onclose = (event) => {
      console.log(`[MP] Disconnected (code: ${event.code})`);
      this.connected = false;
      this.authenticated = false;
      this._stopHeartbeat();
      this.emit('disconnected');
      this._scheduleReconnect();
    };

    this.ws.onerror = (err) => {
      console.error('[MP] WebSocket error');
      this.emit('error', err);
    };
  }

  disconnect() {
    this._pendingToken = null;
    this.reconnectAttempts = Number.MAX_SAFE_INTEGER; // prevent reconnect
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this._stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'client disconnect');
      this.ws = null;
    }
  }

  // ===== Send =====
  _send(type, data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ t: type, d: data }));
  }

  send(type, data) {
    this._send(type, data);
  }

  // ===== Incoming Handler =====
  _handle(msg) {
    const { t, d } = msg;

    switch (t) {
      case 'auth_ok':
        this.authenticated = true;
        this.userId = d.userId;
        this.username = d.username;
        console.log(`[MP] Authenticated as ${d.username}(${d.userId})`);
        this.emit('authenticated', d);
        break;

      case 'party_created':
      case 'party_joined':
        this.partyId = d.partyId;
        this._sendPlayerState();
        this.emit('party_update', { ...d, event: t });
        break;

      case 'party_left':
        this.partyId = null;
        this.emit('party_update', { event: 'left' });
        break;

      case 'party_update':
        this.emit('party_update', d);
        break;

      case 'echo':
        this.emit('echo', d);
        break;

      case 'error':
        console.warn(`[MP] Server error:`, d.msg);
        this.emit('error', d);
        break;

      case 'kicked':
        console.warn(`[MP] Kicked: ${d.msg}`);
        this.emit('kicked', d);
        this.disconnect();
        break;

      // Phase 2+ messages — update __mpState and render
      case 'state_sync':
        window.__mpState = d;
        if (typeof window.renderCombatMP === 'function') {
          window.renderCombatMP(d);
        }
        // Auto-handle combat end
        if (d.isOver && typeof window._handleMPCombatEnd === 'function') {
          setTimeout(() => window._handleMPCombatEnd({ victory: d.victory, gold: d.monGold, exp: d.monExp }), 1500);
        }
        this.emit(t, d);
        break;

      case 'combat_end':
        window.__mpState = null;
        if (typeof window._handleMPCombatEnd === 'function') {
          window._handleMPCombatEnd(d);
        }
        this.emit(t, d);
        break;

      case 'combat_start':
        this.emit(t, d);
        break;

      // Fusion skill messages
      case 'fusion_asked':
        if (window.confirm && confirm(d.fromName + ' requests FUSION: ' + d.skillName + '\n\n' + d.skillDesc + '\n\nAccept?')) {
          this.send('fusion_response', { accept: true });
        } else {
          this.send('fusion_response', { accept: false });
        }
        this.emit(t, d);
        break;

      case 'fusion_result':
        if (typeof window.toast === 'function') {
          window.toast('🔥 FUSION: ' + d.skillName + '! ' + d.dmg + ' dmg!');
        }
        this.emit(t, d);
        break;

      case 'fusion_rejected':
      case 'fusion_sent':
        this.emit(t, d);
        break;

      default:
        console.log(`[MP] Unknown message: ${t}`);
    }
  }

  // ===== Heartbeat =====
  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this._send('pong', {});
      }
    }, HEARTBEAT_INTERVAL);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ===== Reconnect =====
  _scheduleReconnect() {
    if (this.reconnectAttempts >= RECONNECT_DELAYS.length) {
      console.log('[MP] Max reconnect attempts reached');
      this.emit('reconnect_failed');
      return;
    }
    const delay = RECONNECT_DELAYS[this.reconnectAttempts];
    this.reconnectAttempts++;
    console.log(`[MP] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})...`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  // ===== Party Actions =====
  createParty() {
    this.send('party_create');
  }

  joinParty(partyId) {
    this.send('party_join', { partyId });
  }

  leaveParty() {
    this.send('party_leave');
  }

  // ===== State =====
  _sendPlayerState() {
    if (typeof S === 'undefined' || !S) return;
    this.send('player_state', {
      name: S.name, cls: S.cls,
      hp: S.hp, maxHp: S.maxHp,
      mp: S.mp || 0, maxMp: S.maxMp || 50,
      atk: S.atk, def: S.def, spd: S.spd
    });
  }

  getPartyMembers() {
    // This data comes from party_update events
    return this._lastPartyMembers || [];
  }
}

// Singleton
const mpClient = new MPClient();

// Expose globally for inline script access
window.mpClient = mpClient;

export { mpClient };
export default mpClient;
